// src/app/pages/cost-metrics/simulation-panel/simulation-controls/simulation-controls.ts
import { Component, AfterViewInit, computed, effect, signal, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { SliderModule } from 'primeng/slider';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { LocationSelectionService } from '../../service/location-selection.service';
import { CostingDataStore, CostGroupKey } from '../../../core/shared/costing-data.store';
import { InterventionsSnapshot } from '../../../core/shared/intervention.store';

interface UiParameter {
    key: string;
    interface_name: string;
    model_name: string;
    type: 'integer' | 'float' | 'fraction' | 'year';
    default: number;
    range: { min: number; max: number };
    step: number;
    unit: string | null;
}
interface UiIntervention {
    id: number;
    name: string;
    display_name?: string; // NEW: optional display name from JSON (if provided)
    switchKey?: string;
    parameters: UiParameter[];
}
interface UiCategory {
    category: string;
    interventions: UiIntervention[];
}
interface UiCountry {
    country: string;
    city: string;
    flag?: string;
    categories: UiCategory[];
}
interface UiRoot {
    version: number;
    countries: UiCountry[];
}

@Component({
    selector: 'app-simulation-controls',
    standalone: true,
    imports: [FormsModule, AccordionModule, CheckboxModule, SliderModule, ButtonModule, ToastModule],
    providers: [MessageService],
    templateUrl: './simulation-controls.html'
})
export class SimulationControlsComponent implements AfterViewInit {
    constructor(
        private readonly http: HttpClient,
        private readonly locationSel: LocationSelectionService,
        private readonly messageService: MessageService,
        private readonly costingStore: CostingDataStore,
        private cdr: ChangeDetectorRef
    ) {
        this.selectedCountry = signal<string>('Brazil');
        this.selectedCity = signal<string | null>('Sao Paulo');

        effect(() => {
            const min = this.locationSel.selected();
            const newCountry = min?.country || 'Brazil';
            const newCity = min?.city ?? 'Sao Paulo';
            const changed = this.selectedCountry() !== newCountry;
            this.selectedCountry.set(newCountry);
            this.selectedCity.set(newCity);
            if (changed) {
                this.ensureSelectedCountryExists();
                this.resetPerCountryState();
            }
        });
    }

    selectedCountry!: ReturnType<typeof signal<string>>;
    selectedCity!: ReturnType<typeof signal<string | null>>;
    readonly data = signal<UiRoot | null>(null);

    readonly currentCountry = computed<UiCountry | null>(() => {
        const root = this.data();
        const sc = this.selectedCountry();
        if (!root) return null;
        return root.countries.find((c) => c.country === sc) ?? null;
    });

    // NEW: Display-only mapping for intervention names (used when display_name is not provided in JSON)
    private readonly displayNameMap: Record<string, string> = {
        'Awareness raising': 'Improve Health Literacy ',
        'Screening corner': 'PHC Screening Corner',
        'Active search': 'Mobile RF Testing',
        'Diagnostic capacity UBS': 'Increased Diagnostic Capacity',
        'Treatment capacity UBS': 'Increased Treatment Capacity',
        'Caring for all protocol': 'Comprehensive Care Protocol',
        'Remote training': 'Remote Medical Education',
        'Risk stratification tool': 'Risk Stratification Tool',
        'PAP card': 'Patient Self-Management',
        'Adherence combo': 'Treatment Adherence Tools',
        'Governance model': 'Governance Model',
        'Management panel': 'Management Panel'
    };

    // NEW: Helper for display-only name; does not affect logic keyed on intv.name
    displayName(intv: UiIntervention): string {
        const fromJson = (intv.display_name || '').trim();
        if (fromJson) return fromJson;
        return this.displayNameMap[intv.name] ?? intv.name;
    }

    selectedOptions: Record<string, string[]> = {};
    openPanels: Record<string, number | null> = {};
    parameterValues: Record<number, number[]> = {};
    openCategoryIndexes: number = -1;
    activeIndex: number | number[] = -1;

    private relativeTimer: any = null;

    ngOnInit(): void {
        this.http.get<UiRoot>('assets/intervention.json').subscribe({
            next: (res) => {
                this.data.set(res);
                this.ensureSelectedCountryExists();
                this.resetPerCountryState();
            },
            error: (err) => console.error('Failed to load intervention.json', err)
        });

        this.relativeTimer = setInterval(() => {
            this.selectedOptions = { ...this.selectedOptions };
        }, 30000);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.openCategoryIndexes = 0;
            this.activeIndex = 0;
            this.cdr.detectChanges();
        }, 0);
    }

    ngOnDestroy(): void {
        if (this.relativeTimer) clearInterval(this.relativeTimer);
    }

    setActiveIndexSafely(val: number | number[]) {
        this.activeIndex = val;
        Promise.resolve().then(() => this.cdr.detectChanges());
    }

    private ensureSelectedCountryExists(): void {
        const root = this.data();
        if (!root || !root.countries?.length) return;
        const sc = this.selectedCountry();
        if (!root.countries.some((c) => c.country === sc)) {
            this.selectedCountry.set(root.countries[0].country);
            this.persistMinimalSelection();
        }
    }

    onCountryChange(): void {
        this.persistMinimalSelection();
        this.resetPerCountryState();
    }

    onCityChange(city: string | null): void {
        this.selectedCity.set(city);
        this.persistMinimalSelection();
    }

    private persistMinimalSelection(): void {
        const country = this.selectedCountry();
        const city = this.selectedCity() ?? '';
        this.locationSel.setSelected({ country, city });
    }

    resetPerCountryState(): void {
        this.selectedOptions = {};
        this.initializeSelectedOptions();

        // start with no panel open, then defer opening after CD
        this.openCategoryIndexes = -1;
        this.initializeOpenCategories();

        this.openPanels = {};
        this.initializeOpenPanels();
        this.parameterValues = {};
        this.initializeParameterValues();

        // defer panel open to next microtask to prevent NG0100
        Promise.resolve().then(() => {
            this.openCategoryIndexes = 0;
            this.activeIndex = 0;
            this.cdr.detectChanges();
        });
    }

    private initializeSelectedOptions(): void {
        const cc = this.currentCountry();
        if (!cc) return;
        cc.categories.forEach((cat) => (this.selectedOptions[cat.category] ??= []));
    }

    private initializeOpenCategories(): void {
        const cc = this.currentCountry();
        // keep closed initially; will be opened in ngAfterViewInit/resetPerCountryState deferred step
        this.openCategoryIndexes = cc?.categories?.length ? -1 : -1;
    }

    private initializeOpenPanels(): void {
        const cc = this.currentCountry();
        if (!cc) return;
        cc.categories.forEach((cat) => {
            if (!(cat.category in this.openPanels)) this.openPanels[cat.category] = null;
        });
    }

    private initializeParameterValues(): void {
        const cc = this.currentCountry();
        if (!cc) return;
        cc.categories.forEach((cat) => {
            cat.interventions.forEach((intv) => {
                if (!this.parameterValues[intv.id]) {
                    this.parameterValues[intv.id] = intv.parameters.map((p) => p.default);
                }
            });
        });
    }

    togglePanel(category: string, intvId: number): void {
        const cur = this.openPanels[category] ?? null;
        this.openPanels[category] = cur === intvId ? null : intvId;
    }

    isOpen(category: string, intvId: number): boolean {
        return this.openPanels[category] === intvId;
    }

    formatValue(val: number): string {
        if (val === null || val === undefined) return '';
        const abs = Math.abs(val);
        if (abs >= 1_000_000) return (val / 1_000_000).toFixed(0) + 'M';
        if (abs >= 1_000) return (val / 1_000).toFixed(0) + 'k';
        return Number.isInteger(val) ? String(val) : val.toFixed(2);
    }

    buildInterventionsPayload(): any {
        const cc = this.currentCountry();
        if (!cc) return {};

        const parameters: any = {
            switch1: 1,
            switch2: 1,
            switch3: 1,
            switch4: 1,
            switch5: 1,
            switch6: 1,
            switch7: 1,
            switch8: 1,
            switch9: 1,
            switch10: 1,
            'switch learning': 0,
            'switch diagnosis data or intervention': 1,
            'switch treatment data or intervention': 1,
            'switch prehypertension treatment': 1
        };

        const selectedByCategory = this.selectedOptions;
        const isIntvEnabled = (name: string, category: string) => (selectedByCategory[category] ?? []).includes(name);
        const getParamVal = (intv: any, pIdx: number, fallback: number) => this.parameterValues[intv.id]?.[pIdx] ?? fallback;

        const mapIntervention = (category: string, intv: UiIntervention) => {
            // IMPORTANT: use intv.name for logic — displayName is UI-only
            const name = intv.name;
            const enabled = isIntvEnabled(name, category);
            const groupObj: any = {};
            const set = (k: string, v: number | null) => (groupObj[k] = v);
            const val = (idx: number) => getParamVal(intv, idx, intv.parameters[idx].default);

            switch (name) {
                case 'Awareness raising':
                    set('switch campaigns', enabled ? 1 : null);
                    set('number of campaigns per year', val(0));
                    set('number of people reached per campaign', val(1));
                    set('fraction of aware from campaigns', val(2));
                    set('start time campaign 2019', val(3));
                    set('years funded campaign', val(4));
                    parameters['Awareness raising'] = groupObj;
                    break;
                case 'Screening corner':
                    set('switch screening corner', enabled ? 1 : null);
                    set('desired number of screening corner', val(0));
                    set('desired screening corner productivity', val(1));
                    set('start time building screening corner 2018', val(2));
                    set('time to build corner', val(3));
                    parameters['Screening Corner'] = groupObj;
                    break;
                case 'Active search':
                    set('switch active screen', enabled ? 1 : null);
                    set('desired active search productivity', val(0));
                    set('start time building active screen 2018', val(1));
                    set('time to build active screen productivity', val(2));
                    parameters['Active search'] = groupObj;
                    break;
                case 'Diagnostic capacity UBS':
                    set('switch UBS diagnostic productivity', enabled ? 1 : null);
                    set('desired additional UBS diagnostic productivity', val(0));
                    set('percentage of max capacity from initial capacity diagnosis', val(1));
                    set('start time building UBS diagnostic productivity', val(2));
                    set('time to build UBS diagnostic productivity', val(3));
                    parameters['Diagnostic capacity UBS'] = groupObj;
                    break;
                case 'Treatment capacity UBS':
                    set('switch UBS HTN treatment productivity', enabled ? 1 : null);
                    set('desired UBS HTN treatment productivity', val(0));
                    set('percentage of max capacity from initial capacity treatment', val(1));
                    set('start time building UBS HTN treatment productivity', val(2));
                    set('time to build HTN treatment productivity', val(3));
                    parameters['Treatment capacity UBS'] = groupObj;
                    break;
                case 'Caring for all protocol':
                    set('switch training caring for all protocol', enabled ? 1 : null);
                    set('goal effect training caring for all protocol % on control rate', val(0));
                    set('goal effect training caring for all protocol % on diagnostic sensitivity', val(1));
                    set('start time training caring for all protocol', val(2));
                    set('time to reach goal tcap', val(3));
                    parameters['Caring for all protocol'] = groupObj;
                    break;
                case 'Remote training':
                    set('switch remote training', enabled ? 1 : null);
                    set('goal effect remote training % on control rate', val(0));
                    set('goal effect remote training % on diagnostic sensitivity', val(1));
                    set('start time remote training', val(2));
                    set('time to reach goal rt', val(3));
                    parameters['Remote training'] = groupObj;
                    break;
                case 'Risk stratification tool':
                    set('switch risk stratification tool', enabled ? 1 : null);
                    set('goal effect risk stratification tool % on control rate', val(0));
                    set('goal effect risk stratification tool % on diagnostic sensitivity', val(1));
                    set('start time risk stratification tool', val(2));
                    set('time to reach goal rst', val(3));
                    parameters['Risk stratification tool'] = groupObj;
                    break;
                case 'PAP card':
                    set('switch PAP card', enabled ? 1 : null);
                    set('goal effect PAP card % on cease treatment', val(0));
                    set('goal effect PAP card % on resume treatment', val(1));
                    set('start time PAP card 2024', val(2));
                    set('time to reach goal pc', val(3));
                    parameters['PAP card'] = groupObj;
                    break;
                case 'Adherence combo':
                    set('switch adherence combo', enabled ? 1 : null);
                    set('goal effect adherence combo % on cease treatment', val(0));
                    set('goal effect adherence combo % on resume treatment', val(1));
                    set('start time adherence combo 2024', val(2));
                    set('time to reach goal ac', val(3));
                    parameters['Adherence combo'] = groupObj;
                    break;
                case 'Governance model':
                    set('switch management and governance tool', enabled ? 1 : null);
                    set('goal effect management and governance model % on responsiveness', val(0));
                    set('start time management and governance model', val(1));
                    set('time to reach goal mgt', val(2));
                    parameters['Governance model'] = groupObj;
                    break;
                case 'Management panel':
                    set('switch management panel', enabled ? 1 : null);
                    set('goal effect management panel % on responsiveness', val(0));
                    set('start time management panel', val(1));
                    set('time to reach goal mp', val(2));
                    parameters['Management panel'] = groupObj;
                    break;
            }
        };

        cc?.categories.forEach((cat) => cat.interventions.forEach((intv) => mapIntervention(cat.category, intv)));
        return parameters;
    }

    buildCostmetrics(): Record<string, Record<string, number>> {
        const COST_DEFAULTS: Record<string, Record<string, number>> = {
            'Health Sector Cost': {
                'Discount rate': 0.03,
                'Diagnosis cost': 38,
                'Annual hypertension treatment cost': 199,
                'Annual CVD treatment cost': 200,
                'Stroke event cost': 5864,
                'CHD event cost': 1100
            },
            'Awareness raising': { 'Development of the curriculum': 5000, 'Printing training and advocacy materials': 500, 'Salary and transport': 300, 'Training heart friends and ambassadors': 500, 'Communication cost': 200 },
            'Screening Corner': { 'Screening corner equipment': 485, 'Advocacy materials, production and delivery': 109, 'Health facility personnel': 3258, 'Maintenance cost for SC': 100 },
            'Active search': { 'BP measuring device': 997, 'Salaries per outreach activities': 102, 'Transport for outreach activity': 5, 'Communication cost': 200 },
            'Caring for all protocol': {
                'Development of the protocol': 5000,
                'Printing training and advocacy materials protocol': 11,
                'Training caring for all protocol for health staff': 88,
                'Refresher training every year': 118,
                'Communication cost': 200
            },
            'Remote training': { 'Development of the curriculum remote training': 5000, 'Printing training and advocacy materials training': 50, 'Refresher training every remote training': 118, 'Communication cost': 200 },
            'Risk stratification tool': {
                'Development of the risk stratification tool': 5000,
                'Printing training and advocacy material tool': 50,
                'Refresher training city risk stratification tool': 118,
                'Printing material tool': 44,
                'Communication cost': 200
            },
            'PAP card': { 'Development of the PAP card': 5000, 'Initial training to health facilities personnel': 48, 'Printing and production of all materials': 11, 'Communication cost': 200, 'Further printing of materials': 44 },
            'Adherence combo': {
                'Development of the adherence combo package': 5000,
                'Printing training and advocacy material combo': 80,
                'Training adherence combo to health proffesionals': 133,
                'Refresher training every year combo': 118,
                'Further printing of adherence forms': 44,
                'Communication cost': 200
            },
            'Governance model': { 'Salary for governance role': 118 },
            'Management panel': { 'Salary manegerial panel utilisation': 527 }
        };

        const editedCosts: Partial<Record<CostGroupKey, Record<string, number>>> = this.costingStore?.getValues?.() ?? {};

        const mergeGroup = (groupName: CostGroupKey) => {
            const def = COST_DEFAULTS[groupName] || {};
            const cur = editedCosts[groupName] || {};
            const out: Record<string, number> = {};
            Object.keys(def).forEach((k) => (out[k] = def[k]));
            Object.keys(cur).forEach((k) => (out[k] = cur[k]));
            return out;
        };

        return {
            'Health Sector Cost': mergeGroup('Health Sector Cost'),
            'Awareness raising': mergeGroup('Awareness raising'),
            'Screening Corner': mergeGroup('Screening Corner'),
            'Active search': mergeGroup('Active search'),
            'Caring for all protocol': mergeGroup('Caring for all protocol'),
            'Remote training': mergeGroup('Remote training'),
            'Risk stratification tool': mergeGroup('Risk stratification tool'),
            'PAP card': mergeGroup('PAP card'),
            'Adherence combo': mergeGroup('Adherence combo'),
            'Governance model': mergeGroup('Governance model'),
            'Management panel': mergeGroup('Management panel')
        };
    }

    buildSnapshot(): Omit<InterventionsSnapshot, 'activeCount'> {
        const country = this.selectedCountry();
        const city = this.selectedCity();
        const parameters = this.buildInterventionsPayload();
        const costmetrics = this.buildCostmetrics();
        return {
            country: country || 'Brazil',
            city: city ?? 'Sao Paulo',
            selectedOptions: { ...this.selectedOptions },
            parameterValues: JSON.parse(JSON.stringify(this.parameterValues)),
            parameters,
            costmetrics,
            createdAt: Date.now()
        };
    }

    buildNewPayload(): any {
        const country = this.selectedCountry() ?? null;
        const city = this.selectedCity() ?? null;
        const parameters = this.buildInterventionsPayload();
        const Costmetrics = this.buildCostmetrics();
        return { City: city, Country: country, parameters, Costmetrics };
    }
}
